// src/components/cabling/CablingProgressBar.jsx
import React from 'react';
import { COLORS } from '../../constants';

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const TrendingIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

export default function CablingProgressBar({ copperData, fibreData }) {
  const totalCopperQty = copperData?.reduce((sum, c) => sum + (Number(c.qty) || 0), 0) || 0;
  const totalFibreQty = fibreData?.reduce((sum, f) => sum + (Number(f.qty) || 0), 0) || 0;
  
  const copperInstalled = copperData?.reduce((sum, c) => sum + (Number(c.roughedIn) || 0), 0) || 0;
  const fibreInstalled = fibreData?.reduce((sum, f) => sum + (Number(f.completed) || 0), 0) || 0;
  
  const copperProgress = totalCopperQty > 0 ? (copperInstalled / totalCopperQty) * 100 : 0;
  const fibreProgress = totalFibreQty > 0 ? (fibreInstalled / totalFibreQty) * 100 : 0;
  
  const totalCables = totalCopperQty + totalFibreQty;
  const totalInstalled = copperInstalled + fibreInstalled;
  const overallProgress = totalCables > 0 ? (totalInstalled / totalCables) * 100 : 0;

  const sections = [
    { name: 'Copper', progress: copperProgress, installed: copperInstalled, total: totalCopperQty, color: COLORS.primary },
    { name: 'Fibre', progress: fibreProgress, installed: fibreInstalled, total: totalFibreQty, color: COLORS.cyan },
  ];

  // Mock trend calculation (in real app, calculate from historical data)
  const weeklyTrend = copperProgress > 0 || fibreProgress > 0 ? '+8%' : '0%';
  const isTrendingUp = weeklyTrend !== '0%';

  return (
    <div
      style={{
        background: 'white',
        borderRadius: 12,
        padding: 24,
        border: `1px solid ${COLORS.line}`,
        marginBottom: 32,
      }}
    >
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ color: COLORS.success }}><CheckIcon /></div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, margin: 0 }}>Installation Progress</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: COLORS.successLight, borderRadius: 20 }}>
              <TrendingIcon />
              <span style={{ fontSize: 10, fontWeight: 600, color: COLORS.success }}>{weeklyTrend} this week</span>
            </div>
          </div>
          <span style={{ fontSize: 28, fontWeight: 800, color: COLORS.primary }}>{Math.round(overallProgress)}%</span>
        </div>
        
        <div
          style={{
            height: 36,
            background: COLORS.line,
            borderRadius: 18,
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          {sections.map((section) => (
            <div
              key={section.name}
              style={{
                width: totalCables > 0 ? `${(section.progress * (section.total / totalCables))}%` : '0%',
                background: section.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                color: 'white',
                transition: 'width 0.5s ease',
              }}
            >
              {section.progress > 15 && `${Math.round(section.progress)}%`}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
        {sections.map((section) => (
          <div key={section.name} style={{ flex: 1, minWidth: 180 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{section.name}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: section.color }}>{Math.round(section.progress)}%</span>
            </div>
            <div
              style={{
                height: 8,
                background: COLORS.line,
                borderRadius: 4,
                overflow: 'hidden',
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  width: `${section.progress}%`,
                  height: '100%',
                  background: section.color,
                  borderRadius: 4,
                  transition: 'width 0.5s ease',
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: COLORS.textSecondary }}>
              <ClockIcon />
              {section.installed.toLocaleString()} / {section.total.toLocaleString()} installed
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}